/**
 * Pure helpers for the hospital dashboard: IST date handling, bucketing and
 * the consultation counting rules. No network calls here so they stay testable.
 */

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

export interface DateRange {
  /** IST calendar day, yyyy-MM-dd (inclusive) */
  from: string;
  /** IST calendar day, yyyy-MM-dd (inclusive) */
  to: string;
}

export interface TimelinePoint {
  label: string;
  appointments: number;
  consultations: number;
  pa: number;
  vitals: number;
}

export interface EncounterLike {
  id?: string;
  status?: string;
  period?: { start?: string };
  subject?: { reference?: string };
  appointment?: Array<{ reference?: string }>;
}

/** Today's date in IST as yyyy-MM-dd. */
export function todayIst(now: Date = new Date()): string {
  return toIstDateKey(now.toISOString()) as string;
}

/**
 * Validate a yyyy-MM-dd value read from the URL. Returns it only if it is a real
 * calendar day that is not after `max`; otherwise null so the caller can fall back.
 */
export function parseDateParam(value: string | null | undefined, max: string): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) return null;
  return value <= max ? value : null;
}

/** Convert an ISO timestamp to its IST calendar day (yyyy-MM-dd), or null if invalid. */
export function toIstDateKey(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** IST hour of day (0-23) for an ISO timestamp, or null if invalid. */
export function toIstHour(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t + IST_OFFSET_MS).getUTCHours();
}

/** UTC ISO bounds (start inclusive, end inclusive) for an IST day range. */
export function rangeToUtcBounds(range: DateRange): { start: string; end: string } {
  const start = new Date(`${range.from}T00:00:00+05:30`).toISOString();
  const end = new Date(`${range.to}T23:59:59.999+05:30`).toISOString();
  return { start, end };
}

/** True if the ISO instant falls inside the IST day range (both ends inclusive). */
export function isInstantInRange(iso: string | undefined | null, range: DateRange): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  const { start, end } = rangeToUtcBounds(range);
  return t >= Date.parse(start) && t <= Date.parse(end);
}

/** Every IST day (yyyy-MM-dd) between from and to, inclusive. */
export function listDays(range: DateRange): string[] {
  const days: string[] = [];
  const cursor = new Date(`${range.from}T00:00:00Z`).getTime();
  const last = new Date(`${range.to}T00:00:00Z`).getTime();
  if (Number.isNaN(cursor) || Number.isNaN(last) || cursor > last) return days;
  for (let t = cursor; t <= last; t += 24 * 60 * 60 * 1000) {
    days.push(new Date(t).toISOString().slice(0, 10));
  }
  return days;
}

/** Patient id from a FHIR reference like "Patient/123". */
export function patientIdFromRef(ref: string | undefined | null): string | null {
  if (!ref) return null;
  const match = /^Patient\/(.+)$/.exec(ref);
  return match ? match[1] : null;
}

export function hourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${hour < 12 ? 'AM' : 'PM'}`;
}

export type TimelineMetric = 'appointments' | 'consultations' | 'pa' | 'vitals';

/**
 * Build chart buckets. One IST day -> 24 hourly buckets, a range -> one bucket per day.
 * `events` maps each metric to the ISO timestamps that should be counted.
 */
export function buildTimeline(
  range: DateRange,
  events: Record<TimelineMetric, Array<string | undefined | null>>
): TimelinePoint[] {
  const singleDay = range.from === range.to;
  const metrics: TimelineMetric[] = ['appointments', 'consultations', 'pa', 'vitals'];

  const points: TimelinePoint[] = singleDay
    ? Array.from({ length: 24 }, (_, h) => ({
        label: hourLabel(h),
        appointments: 0,
        consultations: 0,
        pa: 0,
        vitals: 0,
      }))
    : listDays(range).map((d) => ({
        label: d,
        appointments: 0,
        consultations: 0,
        pa: 0,
        vitals: 0,
      }));

  const dayIndex = new Map(points.map((p, i) => [p.label, i]));

  for (const metric of metrics) {
    for (const iso of events[metric]) {
      if (singleDay) {
        if (toIstDateKey(iso) !== range.from) continue;
        const hour = toIstHour(iso);
        if (hour !== null) points[hour][metric] += 1;
      } else {
        const key = toIstDateKey(iso);
        const idx = key ? dayIndex.get(key) : undefined;
        if (idx !== undefined) points[idx][metric] += 1;
      }
    }
  }
  return points;
}

export interface ConsultationCounts {
  /** completed + onHold + notSaved */
  total: number;
  /** Encounter `finished`: the doctor saved the record. */
  completed: number;
  /** Encounter in progress, no case sheet reached, appointment On Hold. */
  onHold: number;
  /** Encounter in progress but the doctor reached the case sheet and never saved. */
  notSaved: number;
  /** All counted encounters, for timeline and patient counting. */
  encounters: EncounterLike[];
}

/** True if the encounter points to any appointment whose id is in `appointmentIds`. */
export function isLinkedToAppointmentIn(enc: EncounterLike, appointmentIds: Set<string>): boolean {
  return (enc.appointment ?? []).some((a) => {
    const match = /^Appointment\/(.+)$/.exec(a.reference ?? '');
    return !!match?.[1] && appointmentIds.has(match[1]);
  });
}

/**
 * Classify each distinct Encounter:
 * - completed: `finished`
 * - notSaved: `in-progress` and the doctor reached the case sheet (a case-sheet
 *   summary exists for the encounter) but never clicked Save Record
 * - onHold: `in-progress`, no case-sheet summary, and the linked Appointment is
 *   On Hold in the UI (Appointment status `checked-in` / `on-hold`). The On Hold
 *   button only saves a draft; it never writes a case-sheet summary.
 * Anything else (never started) is not a consultation.
 */
export function countConsultations(
  encounters: EncounterLike[],
  onHoldAppointmentIds: Set<string>,
  encounterIdsWithCaseSheet: Set<string>
): ConsultationCounts {
  const seen = new Set<string>();
  const counted: EncounterLike[] = [];
  let completed = 0;
  let onHold = 0;
  let notSaved = 0;

  for (const enc of encounters) {
    if (!enc.id || seen.has(enc.id)) continue;

    if (enc.status === 'finished') {
      completed += 1;
    } else if (enc.status === 'in-progress') {
      const isOnHold = isLinkedToAppointmentIn(enc, onHoldAppointmentIds);
      if (encounterIdsWithCaseSheet.has(enc.id)) notSaved += 1;
      else if (isOnHold) onHold += 1;
      else continue;
    } else {
      continue;
    }

    seen.add(enc.id);
    counted.push(enc);
  }
  return { total: counted.length, completed, onHold, notSaved, encounters: counted };
}

/** Appointment statuses the UI shows as "On Hold". */
export const ON_HOLD_APPOINTMENT_STATUSES = ['checked-in', 'on-hold'];

/** Distinct count of non-null ids. */
export function countDistinct(ids: Array<string | null | undefined>): number {
  return new Set(ids.filter((id): id is string => !!id)).size;
}
